import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AwaitedNotification, useNotify } from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import DescriptionIcon from '@material-ui/icons/Description'
import { DropzoneArea } from 'material-ui-dropzone'

import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { ServiceResponse } from '../../../../model/serviceResponse'
import { notifyError } from '../../../../redux'
import { downloadPricingTemplate } from '../../pricing.service'

import { UploadedFileResume } from '../../../comunicationCenter/tabs/files/comunicationFiles.model'
import { uploadCommunicationFiles } from '../../../comunicationCenter/tabs/files/comunicationFiles.service'

import { EnumTipoDocumento } from '../../../../api'
import { AllowedFileTypes } from '../../../../config/allowedFileTypes'
import { styles } from './addMasiveDialog.styles'

interface AddMassiveDialogProps {
  onClose: () => void
  //onStartRetriesFetch: () => void
}

export const AddMasiveDialog = ({ onClose }: AddMassiveDialogProps) => {
  const [t] = useTranslation([
    'pricing',
    'pricingDetails',
    'globalerisLibrary',
    'storageHelper',
    'comunicationFiles'
  ])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const { info, awaited } = useNotify()

  const [uploadFile, setUploadFile] = useState<any[] | undefined>(undefined)
  const [uploadedFilesResults, setUploadedFilesResults] = useState<
    UploadedFileResume[] | undefined
  >(undefined)

  // Functions Starts

  const manageFiles = async () => {
    if (uploadFile) {
      const filesUp = uploadFile.map(p => ({ file: p, name: p.name }))

      awaited(
        filesUp.map(p => {
          return {
            title: t('comunicationFiles:notifications.uploadFiles.title', {}),
            message: t('pricingDetails:components.addMasiveDialog.notifications.loading'),
            keyInfo: p.name
          } as AwaitedNotification
        })
      )

      uploadCommunicationFiles(filesUp, EnumTipoDocumento.CargaPrecios).then(
        async (response: ServiceResponse<any>) => {
          if (response.success) {
            const files = response.data
            setUploadedFilesResults(files)
            //info(t('pricingDetails:components.addMasiveDialog.notifications.success'))
            //onStartRetriesFetch()
            onClose()
          } else {
            notifyError(t('storageHelper:notifications.error.upload'))
          }
        }
      )
    }
  }

  // Functions Ends

  // Handlers Starts

  const handleClose = () => {
    onClose()
  }

  const handleDownload = async () => {
    const response = await downloadPricingTemplate()
    if (response.success && response.data) {
      fileDownloadHelper(response.data.name, response.data)
    }
  }

  // Handlers Ends

  // Hooks Starts

  // Hooks Ends

  const DialogTitle = ({ children, ...other }) => {
    return (
      <MuiDialogTitle disableTypography {...other}>
        <Typography variant="h5">{children}</Typography>
        <Tooltip
          title={
            t('pricingDetails:components.addMasiveDialog.customActions.downloadTemplate') || ''
          }
        >
          <IconButton
            aria-label="download"
            className={classes.closeButton}
            onClick={handleDownload}
          >
            <DescriptionIcon />
          </IconButton>
        </Tooltip>
      </MuiDialogTitle>
    )
  }

  return (
    <Dialog
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
      onClose={() => {}}
      open={true}
      className={classes.root}
    >
      <DialogTitle>{t('pricingDetails:components.addMasiveDialog.title')}</DialogTitle>
      <DialogContent>
        <DropzoneArea
          dropzoneClass={classes.dropZone}
          previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
          previewChipProps={{ classes: { root: classes.previewChip } }}
          showAlerts={false}
          showPreviews={true}
          showPreviewsInDropzone={false}
          useChipsForPreview
          previewText={t('pricingDetails:components.addMasiveDialog.upload.selectedFiles')}
          dropzoneText={t('pricingDetails:components.addMasiveDialog.upload.dropText', {
            maxCount: 1
          })}
          filesLimit={1}
          acceptedFiles={['.xlsx', '.xlsm']}
          onChange={file => {
            setUploadFile(file)
          }}
          initialFiles={uploadFile}
          maxFileSize={AllowedFileTypes.communicationFiles.maxSizeMB * 1024 * 1024}
        />
      </DialogContent>
      <DialogActions className={classes.dialogActionsRoot}>
        <Button size="small" onClick={handleClose}>
          {t('pricingDetails:components.addMasiveDialog.mainActions.cancel')}
        </Button>
        <Button
          size="small"
          onClick={async () => {
            manageFiles()
          }}
          color="primary"
          disabled={!uploadFile || uploadFile?.length === 0}
        >
          {t('pricingDetails:components.addMasiveDialog.mainActions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
